import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { ToastrService } from "ngx-toastr";
import { MiscService } from "app/shared/misc.service";
import { SettingsService } from "app/modules/settings/settings.service";

const VERIFIED_KEY_PREFIX = "secure_tab_otp_verified_";

@Component({
  selector: "app-otp-gate",
  templateUrl: "./otp-gate.component.html",
  styleUrls: ["./otp-gate.component.scss"],
})
export class OtpGateComponent implements OnInit, OnDestroy {
  @Input() feature = "default";
  @Input() title = "Additional verification required";
  @Input() description =
    "For your security, this section requires a one-time verification code sent to your registered email address.";

  verified = false;
  otpSent = false;
  loading = false;
  resending = false;
  otp = "";
  resendCooldown = 0;
  email: string = "";
  otpConfig = { length: 6, allowNumbersOnly: true, inputClass: "otp-input" };

  private resendTimer: any;
  private merchantId: string = "";

  constructor(
    private settingsService: SettingsService,
    private miscService: MiscService,
    private toastr: ToastrService,
  ) {}

  ngOnInit(): void {
    const merchant = this.miscService.getMerchantDetails();
    this.merchantId = merchant?.merchant_id;
    this.email = localStorage.getItem("user_email") || merchant?.director?.email;
    this.verified =
      sessionStorage.getItem(this.storageKey()) === this.currentSessionId();
  }

  ngOnDestroy(): void {
    if (this.resendTimer) {
      clearInterval(this.resendTimer);
    }
  }

  private storageKey(): string {
    return `${VERIFIED_KEY_PREFIX}${this.feature}`;
  }

  private currentSessionId(): string {
    return localStorage.getItem("session_id") || "";
  }

  sendOtp(): void {
    if (!this.email || !this.merchantId) {
      this.toastr.error(
        "Unable to identify your account, please refresh and try again",
      );
      return;
    }
    this.loading = true;
    this.settingsService
      .requestSecureOtp({ email: this.email, merchant_id: this.merchantId })
      .subscribe({
        next: () => {
          this.otpSent = true;
          this.loading = false;
          this.toastr.success(
            "A verification code has been sent to your email",
          );
          this.startResendCooldown();
        },
        error: () => {
          this.loading = false;
          this.toastr.error(
            "Sorry, couldn't send verification code. Please try again",
          );
        },
      });
  }

  resendOtp(): void {
    if (
      this.resendCooldown > 0 ||
      this.resending ||
      !this.email ||
      !this.merchantId
    ) {
      return;
    }
    this.resending = true;
    this.settingsService
      .requestSecureOtp({ email: this.email, merchant_id: this.merchantId })
      .subscribe({
        next: () => {
          this.resending = false;
          this.toastr.success(
            "A new verification code has been sent to your email",
          );
          this.startResendCooldown();
        },
        error: () => {
          this.resending = false;
          this.toastr.error(
            "Sorry, couldn't resend verification code. Please try again",
          );
        },
      });
  }

  private startResendCooldown(): void {
    this.resendCooldown = 30;
    if (this.resendTimer) {
      clearInterval(this.resendTimer);
    }
    this.resendTimer = setInterval(() => {
      this.resendCooldown -= 1;
      if (this.resendCooldown <= 0) {
        clearInterval(this.resendTimer);
      }
    }, 1000);
  }

  verifyOtp(): void {
    if (!this.otp || !this.otp.trim()) {
      this.toastr.error(
        "Please enter the verification code sent to your email",
      );
      return;
    }
    this.loading = true;
    this.settingsService
      .verifySecureOtp({ email: this.email, token: this.otp.trim() })
      .subscribe({
        next: () => {
          this.loading = false;
          this.verified = true;
          sessionStorage.setItem(this.storageKey(), this.currentSessionId());
          this.toastr.success("Verified successfully");
        },
        error: () => {
          this.loading = false;
          this.toastr.error("Incorrect or expired code, please try again");
        },
      });
  }
}
